"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { z } from "zod";
import { isAfter, parseISO, startOfToday } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  AmountStep,
  DescriptionStep,
  LessonStep,
  NotificationStep,
  PayerStep,
  SummaryStep,
  TypeStep,
} from "./steps";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { toast } from "@/utils/customToast";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { assignApiErrorsToFormFields } from "@/features/auth/utils/errorHandler";

type CreatePaymentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (paymentId: string) => Promise<void> | void;
};

// moved fetching into step components; no local listing types needed here

// Base schema for per-step validation
const paymentBaseSchema = (t: any) =>
  z.object({
    type: z.enum(["lesson", "other"], {
      required_error: t("finances:createPayment.validation.typeRequired"),
    }),
    payer_user_id: z
      .string({ required_error: t("finances:createPayment.validation.payerRequired") })
      .min(1, t("finances:createPayment.validation.payerRequired")),
    lesson_id: z.string().optional(),
    amount: z
      .string({ required_error: t("finances:createPayment.validation.amountRequired") })
      .refine(
        (v) => /^\d+([.,]\d{1,2})?$/.test(v.trim()),
        t("finances:createPayment.validation.amountNumeric"),
      )
      .refine(
        (v) => parseFloat(v.replace(",", ".")) >= 0.01,
        t("finances:createPayment.validation.amountMin"),
      ),
    currency: z.enum(["USD", "EUR", "GBP", "PLN"], {
      required_error: t("finances:createPayment.validation.currencyRequired"),
    }),
    payment_due: z.string().optional(),
    description: z
      .string()
      .max(2000, { message: t("finances:createPayment.validation.descriptionMax") })
      .optional(),
    should_notify_payer: z.boolean().default(true),
    notification_message: z
      .string()
      .max(1000, { message: t("finances:createPayment.validation.notificationMessageMax") })
      .optional(),
    payment_destination: z
      .string()
      .max(255, { message: t("finances:createPayment.validation.paymentDestinationMax") })
      .optional(),
  });

// Final submit schema with cross-field validation
const paymentSubmitSchema = (t: any) =>
  paymentBaseSchema(t).superRefine((data, ctx) => {
    if (data.type === "lesson" && !data.lesson_id) {
      ctx.addIssue({
        code: "custom",
        path: ["lesson_id"],
        message: t("finances:createPayment.validation.lessonRequired"),
      });
    }
    if (data.type === "other" && !data.description?.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["description"],
        message: t("finances:createPayment.validation.descriptionRequired"),
      });
    }
    if (data.payment_due) {
      const parsed = parseISO(`${data.payment_due}T00:00:00`);

      if (!isAfter(parsed, startOfToday())) {
        ctx.addIssue({
          code: "custom",
          path: ["payment_due"],
          message: t("finances:createPayment.validation.dueAfterToday"),
        });
      }
    }
  });

export default function CreatePaymentDialog({
  open,
  onOpenChange,
  onCreated,
}: CreatePaymentDialogProps) {
  const { t } = useTranslation(["finances", "common", "workspaces"]);
  const baseSchema = useMemo(() => paymentBaseSchema(t), [t]);
  const submitSchema = useMemo(() => paymentSubmitSchema(t), [t]);

  type FormData = z.infer<ReturnType<typeof paymentBaseSchema>>;

  const form = useForm<FormData>({
    resolver: zodResolver(submitSchema),
    defaultValues: {
      type: "lesson",
      payer_user_id: "",
      lesson_id: "",
      amount: "",
      currency: "USD",
      payment_due: "",
      description: "",
      should_notify_payer: true,
      notification_message: "",
      payment_destination: "",
    },
    mode: "onChange",
  });

  // Multi-step state
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = 6;

  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset();
      setCurrentStep(0);
    }
  }, [open]);

  // Reset form when type changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "type" && value.type) {
        // Reset all fields except type
        form.setValue("lesson_id", "");
        form.setValue("payer_user_id", "");
        form.setValue("amount", "");
        form.setValue("payment_due", "");
        form.setValue("description", "");
        form.setValue("should_notify_payer", true);
        form.setValue("notification_message", "");
        form.setValue("payment_destination", "");

        // Clear errors
        form.clearErrors();
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  // Step schema mapping for per-step validation (similar to CreateProfileForm)
  const getStepSchema = (step: number, values: FormData) => {
    switch (step) {
      case 0:
        return baseSchema.pick({ type: true });
      case 1:
        return baseSchema.pick({ amount: true, currency: true });
      case 2:
        return values.type === "lesson"
          ? baseSchema.pick({ lesson_id: true })
          : baseSchema.pick({ description: true });
      case 3:
        return baseSchema.pick({ payer_user_id: true, payment_due: true });
      case 4:
        return baseSchema.pick({
          should_notify_payer: true,
          payment_destination: true,
          notification_message: true,
        });
      case 5:
        return z.object({});
      default:
        return z.object({});
    }
  };

  const validateStep = (step: number): boolean => {
    const values = form.getValues();
    const schema = getStepSchema(step, values);

    try {
      schema.parse(values);

      // Additional business logic validation for specific steps
      if (step === 2 && values.type === "lesson" && !values.lesson_id) {
        form.setError("lesson_id", {
          type: "manual",
          message: t("finances:createPayment.validation.lessonRequired"),
        });

        return false;
      }

      if (step === 2 && values.type === "other" && !values.description?.trim()) {
        form.setError("description", {
          type: "manual",
          message: t("finances:createPayment.validation.descriptionRequired"),
        });

        return false;
      }

      // Clear errors for the current step
      const stepFields = Object.keys(schema.shape);

      stepFields.forEach((field) => {
        form.clearErrors(field as keyof FormData);
      });

      return true;
    } catch (err: any) {
      if (Array.isArray(err?.errors)) {
        err.errors.forEach((issue: any) => {
          const field = issue.path?.[0];

          if (field) {
            form.setError(field as keyof FormData, { type: "manual", message: issue.message });
          }
        });
      }

      return false;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      const isValid = validateStep(currentStep);

      if (isValid) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Function to determine which step has validation errors
  const getStepWithError = (validationErrors: any, fieldMapping: any): number => {
    const errorFields = Object.keys(validationErrors);

    // Check each step for errors
    for (let step = 0; step < totalSteps; step++) {
      const stepFields = Object.keys(getStepSchema(step, form.getValues()));
      const hasErrorInStep = stepFields.some((field) => {
        // Check if any API error field maps to this form field
        return Object.entries(fieldMapping).some(
          ([apiField, formField]) => formField === field && errorFields.includes(apiField),
        );
      });

      if (hasErrorInStep) {
        return step;
      }
    }

    // Default to first step if no specific step found
    return 0;
  };

  const handleSubmit = async (values: FormData) => {
    setIsSubmitting(true);
    try {
      const payload = {
        status: "created",
        type: values.type,
        amount: parseFloat(values.amount.replace(",", ".")),
        currency: values.currency,
        payer_user_id: values.payer_user_id,
        should_notify_payer: values.should_notify_payer,
        ...(values.type === "lesson" && { lesson_id: values.lesson_id }),
        ...(values.payment_due && { payment_due: values.payment_due }),
        ...(values.payment_destination?.trim() && {
          payment_destination: values.payment_destination.trim(),
        }),
        ...(values.notification_message?.trim() && {
          notification_message: values.notification_message.trim(),
        }),
        ...(values.type === "other" &&
          values.description?.trim() && {
            description: values.description.trim(),
          }),
      };

      const { data } = await axiosApiInstance.post(
        API_ENDPOINTS.TUTOR.FINANCES.PAYMENTS_CREATE,
        payload,
      );

      toast.success(t("finances:createPayment.created"));
      onOpenChange(false);

      if (onCreated) {
        await onCreated(data?.data?.id);
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const errorData = error?.response?.data;

      if (status === 401) {
        toast.error(t("common:errors.unauthenticated"));
        onOpenChange(false);
      } else if (status === 403) {
        if (errorData?.error === "insufficient_permissions") {
          toast.error(t("common:errors.permissionDenied"));
        } else if (errorData?.error === "invalid_payment_plan") {
          toast.error(t("finances:createPayment.errors.invalidPaymentPlan"));
        } else {
          toast.error(errorData?.message || t("common:errors.permissionDenied"));
        }
        onOpenChange(false);
      } else if (status === 404) {
        if (errorData?.error === "lesson_not_found_for_tutor") {
          toast.error(t("finances:createPayment.errors.lessonNotFound"));
          console.log("Lesson not found for tutor, navigating to step 2 (lesson selection)");
          // Go to lesson selection step
          setCurrentStep(2);
        } else {
          toast.error(errorData?.message || t("common:errors.notFound"));
        }
      } else if (status === 422) {
        if (errorData?.errors) {
          const validationErrors = errorData.errors;

          // Map API error fields to form fields
          const fieldMapping = {
            status: "status",
            type: "type",
            payer_user_id: "payer_user_id",
            lesson_id: "lesson_id",
            amount: "amount",
            currency: "currency",
            payment_due: "payment_due",
            description: "description",
            should_notify_payer: "should_notify_payer",
            notification_message: "notification_message",
            payment_destination: "payment_destination",
          };

          // Use assignApiErrorsToFormFields to properly handle API errors
          assignApiErrorsToFormFields(validationErrors, form.setError, fieldMapping);
          toast.error(t("finances:createPayment.errors.validationFailed"));

          // Navigate to the step that has the error
          const stepWithError = getStepWithError(validationErrors, fieldMapping);

          console.log("Validation errors:", validationErrors);
          console.log("Navigating to step:", stepWithError);
          setCurrentStep(stepWithError);
        } else if (errorData?.error === "payer_not_found") {
          toast.error(t("finances:createPayment.errors.payerNotFound"));
          console.log("Payer not found, navigating to step 3 (payer selection)");
          // Go to payer selection step
          setCurrentStep(3);
        } else if (errorData?.error === "payer_not_student_for_tutor") {
          toast.error(t("finances:createPayment.errors.payerNotStudent"));
          console.log("Payer not student for tutor, navigating to step 3 (payer selection)");
          // Go to payer selection step
          setCurrentStep(3);
        } else {
          toast.error(errorData?.message || t("finances:createPayment.errors.validationFailed"));
        }
      } else {
        toast.error(errorData?.message || t("finances:createPayment.errors.generic"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Steps configuration
  // Dynamic step that decides based on current type without rebuilding steps array
  const StepTwo = () => {
    const type = form.watch("type");

    return type === "lesson" ? (
      <LessonStep control={form.control} />
    ) : (
      <DescriptionStep control={form.control} />
    );
  };

  const steps = useMemo(
    () => [
      { id: 0, render: () => <TypeStep control={form.control} /> },
      { id: 1, render: () => <AmountStep control={form.control} /> },
      { id: 2, render: () => <StepTwo /> },
      { id: 3, render: () => <PayerStep control={form.control} /> },
      { id: 4, render: () => <NotificationStep control={form.control} /> },
      { id: 5, render: () => <SummaryStep control={form.control} /> },
    ],
    [form.control],
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-background/60 text-accent-foreground flex h-[min(85dvh,720px)] min-w-[min(90dvw,1024px)] overflow-hidden p-0 shadow-2xl backdrop-blur-xl max-sm:h-dvh max-sm:w-screen max-sm:rounded-none"
      >
        <ScrollArea className="flex-1">
          <motion.div
            animate={{ opacity: 1 }}
            className="relative h-[min(85dvh,550px)] min-w-[min(90dvw,1024px)] max-sm:h-[90dvh]"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Background effects */}
            <motion.div
              key={`circle-1-${currentStep}`}
              layout
              animate={{ opacity: 0.25 }}
              className={`pointer-events-none absolute -top-40 -right-40 size-120 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20`}
              initial={{ opacity: 0 }}
              layoutId="payment-circle-1"
              transition={{ layout: { duration: 0.5 } }}
            />
            <motion.div
              key={`circle-2-${currentStep}`}
              layout
              animate={{ opacity: 0.25 }}
              className={`pointer-events-none absolute -bottom-40 -left-40 size-120 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20`}
              initial={{ opacity: 0.2 }}
              layoutId="payment-circle-2"
              transition={{ layout: { duration: 0.5 } }}
            />
            {/* Content */}
            <div className="flex h-full flex-col">
              <div className="flex flex-1 flex-col justify-center">
                <Form {...form}>
                  <form className="h-full" onSubmit={form.handleSubmit(handleSubmit)}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentStep}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex h-full items-center justify-center"
                        exit={{ opacity: 0, y: -20 }}
                        initial={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                      >
                        <div className="w-full max-w-4xl">{steps[currentStep].render()}</div>
                      </motion.div>
                    </AnimatePresence>
                  </form>
                </Form>
              </div>
            </div>
          </motion.div>
          <div className="bg-background absolute bottom-0 z-50 flex h-16 w-full flex-shrink-0 items-center justify-between px-6 py-3 backdrop-blur-xl">
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button disabled={isSubmitting} variant="outline" onClick={handlePrevious}>
                  <Icon className="sm:mr-2" height="1rem" icon="tabler:arrow-left" width="1rem" />
                  <span className={"max-sm:hidden"}>{t("common:previousPage")}</span>
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button disabled={isSubmitting} variant="outline" onClick={handleClose}>
                {t("common:cancel")}
              </Button>
              {currentStep < totalSteps - 1 ? (
                <Button disabled={isSubmitting} variant="foreground" onClick={handleNext}>
                  {t("common:next")}
                  <Icon className="ml-2" height="1rem" icon="tabler:arrow-right" width="1rem" />
                </Button>
              ) : (
                <Button
                  className="gap-2"
                  disabled={isSubmitting}
                  variant="foreground"
                  onClick={form.handleSubmit(handleSubmit)}
                >
                  {isSubmitting ? (
                    <>
                      <Icon
                        className="animate-spin"
                        height="1rem"
                        icon="tabler:loader-2"
                        width="1rem"
                      />
                      {t("common:creating")}
                    </>
                  ) : (
                    <>
                      <Icon height="1rem" icon="tabler:check" width="1rem" />
                      {t("common:confirm")}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
